import { useState } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setSubmitted(true);
      setEmail("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1A2B1F] text-white">
      {/* Newsletter Strip */}
      <div className="border-b border-white/10 py-14 px-6">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-white mb-2">
              Stay Connected
            </h3>
            <p className="text-white/60 text-sm max-w-md">
              Receive insights on African Diaspora wealth creation, trading education, and cross-border investment opportunities.
            </p>
          </div>
          {submitted ? (
            <div className="flex items-center gap-3 text-[#C9A961]">
              <i className="ri-checkbox-circle-line text-2xl" />
              <span className="text-sm font-medium">Thank you for subscribing!</span>
            </div>
          ) : (
            <form
              onSubmit={handleNewsletter}
              className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="px-5 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#C9A961] w-full sm:w-80"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 rounded-md bg-[#C9A961] text-[#2B2D2F] text-sm font-semibold hover:bg-[#b8943f] transition-colors whitespace-nowrap cursor-pointer"
              >
                {submitting ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16 px-6">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/assets/brand/logo.png"
              alt="Harambee Global Ventures"
              className="h-12 w-auto object-contain mb-4"
            />
            <p className="text-white/50 text-sm leading-relaxed mb-5">
              A cross-border investment, education, and advisory platform for the African Diaspora and emerging investors.
            </p>
            <p className="text-white/30 text-xs">
              An international subsidiary of<br />
              <span className="text-white/50">7 Hills Development Group LLC</span>
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">Company</h4>
            <ul className="space-y-3">
              {[
                { label: "About Us", path: "/about" },
                { label: "Leadership", path: "/leadership" },
                { label: "Partnerships", path: "/partnerships" },
                { label: "Insights", path: "/insights" },
                { label: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/50 text-sm hover:text-[#C9A961] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">Services</h4>
            <ul className="space-y-3">
              {[
                { label: "Trading Academy", path: "/trading-academy" },
                { label: "Advisory Services", path: "/advisory" },
                { label: "Family Office", path: "/services" },
                { label: "Private Credit", path: "/services" },
                { label: "Workforce Development", path: "/services" },
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="text-white/50 text-sm hover:text-[#C9A961] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-[#C9A961] text-base" />
                </div>
                <span className="text-white/50 text-sm">Nairobi, Kenya<br />East Africa Hub</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-[#C9A961] text-base" />
                </div>
                <a href="mailto:info@harambeeglobal.com" className="text-white/50 text-sm hover:text-[#C9A961] transition-colors cursor-pointer">
                  info@harambeeglobal.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-global-line text-[#C9A961] text-base" />
                </div>
                <a
                  href="https://harambeeglobal.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/50 text-sm hover:text-[#C9A961] transition-colors cursor-pointer"
                >
                  harambeeglobal.com
                </a>
              </li>
              <li className="flex items-center gap-4 mt-2">
                <a href="#" rel="nofollow" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 hover:border-[#C9A961] hover:text-[#C9A961] text-white/50 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-sm" />
                </a>
                <a href="#" rel="nofollow" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 hover:border-[#C9A961] hover:text-[#C9A961] text-white/50 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-sm" />
                </a>
                <a href="#" rel="nofollow" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 hover:border-[#C9A961] hover:text-[#C9A961] text-white/50 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-sm" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6 px-6">
        <div className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Harambee Global Ventures. All rights reserved. A subsidiary of 7 Hills Development Group LLC.
          </p>
          <div className="flex items-center gap-5">
            <a href="#" rel="nofollow" className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" rel="nofollow" className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">Terms of Use</a>
            <a href="#" rel="nofollow" className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
